<template>
  <detail-page-shell
    :back-to="projectReturnPath || ''"
    :use-history-back="projectDetailBackUsesHistory"
    :back-text="projectBackLabel"
    compact-header
  >
    <template #actions>
      <el-button size="small" @click="$router.push('/finance/overview')">{{ $t('nav.finance') }}</el-button>
      <el-button v-if="canManageProjectFinance" type="primary" size="small" @click="recalc">{{ $t('projectDetailPage.recalc') }}</el-button>
    </template>

    <div v-loading="loading">
      <div class="summary-grid">
        <div class="summary-card">
          <span>{{ $t('projectPage.contractAmountTotal') }}</span>
          <strong class="summary-card__contract-line">
            <span>{{ formatUsd(summary.totalSalesAmount) }}</span>
            <span
              v-if="hasApprovedContractChange(summary)"
              class="summary-card__contract-parts"
            >（{{ formatUsd(summary.contractAmount) }} + {{ formatUsd(summary.changeOrderAmount) }}）</span>
          </strong>
        </div>
        <div class="summary-card">
          <span>{{ $t('projectPage.totalReceived') }}</span>
          <strong>{{ formatUsd(summary.totalReceived) }}</strong>
        </div>
        <div class="summary-card">
          <span>{{ $t('projectPage.operationalExpense') }}</span>
          <strong>{{ formatUsd(summary.totalExpense) }}</strong>
        </div>
        <div class="summary-card">
          <span>{{ $t('projectPage.leadCost') }}</span>
          <strong>{{ formatUsd(summary.leadCost) }}</strong>
        </div>
        <div class="summary-card">
          <span>{{ $t('projectPage.profit') }}</span>
          <strong>{{ formatUsd(summary.profit) }}</strong>
        </div>
      </div>

      <el-card shadow="never">
        <el-tabs v-model="tab">
          <el-tab-pane :label="$t('projectPage.overview')" name="overview">
            <el-descriptions v-if="p" border :column="2">
              <el-descriptions-item :label="$t('projectDetailPage.projectCode')">{{ p.projectCode }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.projectName')">{{ p.projectName }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.projectStatus')">{{ labelProjectStatus(p.projectStatusCode) }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.contractNo')">{{ p.linkedContractNo || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.customerName')">{{ p.linkedCustomerDisplayName || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.customerPhone')">{{ p.linkedCustomerPhone || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.customerEmail')">{{ p.linkedCustomerEmail || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.pm')">{{ p.projectManagerUserName || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectDetailPage.assignedSub')">{{ p.subcontractorDisplayName || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectPage.contractAmountTotal')">
                <span>{{ formatUsd(summary.totalSalesAmount) }}</span>
                <span
                  v-if="hasApprovedContractChange(summary)"
                  class="desc-contract-parts"
                >（{{ formatUsd(summary.contractAmount) }} + {{ formatUsd(summary.changeOrderAmount) }}）</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('projectPage.leadCost')">{{ formatUsd(summary.leadCost) }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectPage.operationalExpense')">{{ formatUsd(summary.totalExpense) }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectPage.profit')">{{ formatUsd(summary.profit) }}</el-descriptions-item>
              <el-descriptions-item :label="$t('projectPage.pmCommission')">{{ formatUsd(summary.pmCommission) }}</el-descriptions-item>
            </el-descriptions>

            <el-card shadow="never" class="block-card">
              <div slot="header">{{ $t('projectPage.changeOrders') }}</div>
              <el-table :data="workspace.changeOrders" size="small" border>
                <el-table-column prop="changeNo" :label="$t('projectDetailPage.changeOrderNo')" width="140" />
                <el-table-column prop="descriptionText" :label="$t('grid.description')" min-width="220" />
                <el-table-column :label="$t('common.amount')" width="120" align="right">
                  <template slot-scope="scope">{{ formatUsd(scope.row.amountDelta) }}</template>
                </el-table-column>
                <el-table-column prop="approvalStatusCode" :label="$t('common.status')" width="120">
                  <template slot-scope="scope">{{ labelChangeOrderApproval(scope.row.approvalStatusCode) }}</template>
                </el-table-column>
              </el-table>
            </el-card>
          </el-tab-pane>

          <el-tab-pane :label="$t('projectPage.financials')" name="financials">
            <div class="financial-grid">
              <el-card shadow="never">
                <div slot="header" class="table-head">
                  <span>{{ $t('projectPage.expenses') }}</span>
                  <el-button v-if="canManageProjectFinance" type="primary" size="small" @click="openExpenseDialog()">{{ $t('common.create') }}</el-button>
                </div>
                <el-table :data="workspace.expenses" size="small" border>
                  <el-table-column prop="expenseDate" :label="$t('common.date')" width="110" />
                  <el-table-column :label="$t('projectDetailPage.expenseKindCol')" width="120" show-overflow-tooltip>
                    <template slot-scope="scope">{{ labelExpenseKind(scope.row.expenseTypeCode) }}</template>
                  </el-table-column>
                  <el-table-column prop="payeeName" :label="$t('projectDetailPage.payee')" min-width="160" />
                  <el-table-column prop="paymentMethod" :label="$t('projectDetailPage.method')" width="120" />
                  <el-table-column :label="$t('common.amount')" width="120" align="right">
                    <template slot-scope="scope">{{ formatUsd(scope.row.amount) }}</template>
                  </el-table-column>
                  <el-table-column :label="$t('projectDetailPage.colAttachments')" width="110" align="center">
                    <template slot-scope="scope">
                      <el-button
                        v-if="attachmentCountForExpense(scope.row.id) > 0"
                        type="text"
                        size="small"
                        @click="openExpenseAttachments(scope.row)"
                      >{{ attachmentCountForExpense(scope.row.id) }}</el-button>
                      <span v-else class="text-muted">0</span>
                    </template>
                  </el-table-column>
                  <el-table-column v-if="canManageProjectFinance" :label="$t('common.actions')" width="120">
                    <template slot-scope="scope">
                      <el-button type="text" size="small" @click="openExpenseDialog(scope.row)">{{ $t('common.edit') }}</el-button>
                      <el-button type="text" size="small" @click="removeExpense(scope.row)">{{ $t('common.delete') }}</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-card>

              <el-card shadow="never">
                <div slot="header" class="table-head">
                  <span>{{ $t('projectPage.paymentRecords') }}</span>
                  <el-button v-if="canManageProjectFinance" type="primary" size="small" @click="openPaymentDialog()">{{ $t('common.create') }}</el-button>
                </div>
                <el-table :data="workspace.payments" size="small" border class="payment-records-table">
                  <el-table-column prop="paymentDate" :label="$t('common.date')" width="112" />
                  <el-table-column :label="$t('projectDetailPage.paymentPlan')" min-width="128" show-overflow-tooltip>
                    <template slot-scope="scope">{{ paymentPlanLabel(scope.row.paymentPlanId) }}</template>
                  </el-table-column>
                  <el-table-column :label="$t('common.amount')" min-width="110" align="right">
                    <template slot-scope="scope">{{ formatUsd(scope.row.amount) }}</template>
                  </el-table-column>
                  <el-table-column prop="paymentMethod" :label="$t('projectDetailPage.method')" min-width="108" show-overflow-tooltip />
                  <el-table-column prop="referenceNo" :label="$t('projectDetailPage.reference')" min-width="128" show-overflow-tooltip />
                  <el-table-column :label="$t('projectDetailPage.invoice')" min-width="128" align="center">
                    <template slot-scope="scope">
                      <template v-if="scope.row.invoiceId">
                        <el-button type="text" size="small" @click.stop="openInvoicePreview(scope.row.invoiceId)">{{ $t('common.preview') }}</el-button>
                        <el-button type="text" size="small" @click.stop="openInvoiceDownload(scope.row.invoiceId)">{{ $t('common.download') }}</el-button>
                      </template>
                      <span
                        v-else-if="canManageProjectFinance"
                        class="payment-invoice-cell payment-invoice-cell--action"
                        role="button"
                        tabindex="0"
                        :title="$t('projectDetailPage.paymentInvoiceClickUpload')"
                        @click.stop="openPaymentInvoiceDialog(scope.row)"
                        @keydown.enter.prevent.stop="openPaymentInvoiceDialog(scope.row)"
                      >{{ $t('projectDetailPage.invoiceNotAttached') }}</span>
                      <span v-else class="payment-invoice-cell">{{ $t('projectDetailPage.invoiceNotAttached') }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column :label="$t('projectDetailPage.colAttachments')" width="110" align="center">
                    <template slot-scope="scope">
                      <el-button
                        v-if="attachmentCountForPayment(scope.row.id) > 0"
                        type="text"
                        size="small"
                        @click="openPaymentAttachments(scope.row)"
                      >{{ attachmentCountForPayment(scope.row.id) }}</el-button>
                      <span v-else class="text-muted">0</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="remarkText" :label="$t('projectDetailPage.remark')" min-width="140" show-overflow-tooltip />
                  <el-table-column v-if="canManageProjectFinance" :label="$t('common.actions')" width="120">
                    <template slot-scope="scope">
                      <el-button type="text" size="small" @click="openPaymentDialog(scope.row)">{{ $t('common.edit') }}</el-button>
                      <el-button type="text" size="small" @click="removePayment(scope.row)">{{ $t('common.delete') }}</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-card>
            </div>

            <el-card shadow="never" class="block-card">
              <div slot="header">{{ $t('projectPage.paymentPlan') }}</div>
              <el-table :data="workspace.paymentPlans" size="small" border class="payment-plan-table">
                <el-table-column prop="nodeSeq" label="#" width="56" align="center" />
                <el-table-column prop="nodeName" :label="$t('projectDetailPage.planNodeName')" min-width="140" show-overflow-tooltip />
                <el-table-column :label="$t('projectDetailPage.planPlannedAmt')" min-width="128" align="right">
                  <template slot-scope="scope">{{ formatUsd(scope.row.plannedAmount) }}</template>
                </el-table-column>
                <el-table-column prop="plannedDate" :label="$t('projectDetailPage.planPlannedDateCol')" min-width="148" />
                <el-table-column :label="$t('common.status')" min-width="112">
                  <template slot-scope="scope">{{ labelPaymentPlanStatus(scope.row.paymentStatusCode) }}</template>
                </el-table-column>
                <el-table-column :label="$t('common.actions')" width="100" align="center" fixed="right">
                  <template slot-scope="scope">
                    <el-button
                      v-if="canManageProjectFinance && isPaymentPlanPlanning(scope.row)"
                      type="primary"
                      plain
                      size="mini"
                      @click="openReceivePaymentForPlan(scope.row)"
                    >{{ $t('projectDetailPage.receivePayment') }}</el-button>
                    <span v-else class="payment-plan-actions__empty">—</span>
                  </template>
                </el-table-column>
              </el-table>
            </el-card>

            <el-card v-if="workspace.invoices && workspace.invoices.length" shadow="never" class="block-card">
              <div slot="header">{{ $t('projectDetailPage.invoiceListTitle') }}</div>
              <el-table :data="workspace.invoices" size="small" border>
                <el-table-column prop="invoiceNo" :label="$t('projectDetailPage.invoiceColNo')" min-width="128" show-overflow-tooltip />
                <el-table-column prop="invoiceDate" :label="$t('common.date')" width="118" />
                <el-table-column :label="$t('common.amount')" width="120" align="right">
                  <template slot-scope="scope">{{ formatUsd(scope.row.amount) }}</template>
                </el-table-column>
                <el-table-column :label="$t('projectDetailPage.invoiceMainFile')" width="132" align="center">
                  <template slot-scope="scope">
                    <span v-if="scope.row.fileUrl">{{ $t('projectDetailPage.invoiceAttached') }}</span>
                    <span v-else class="text-muted">{{ $t('projectDetailPage.invoiceNotAttached') }}</span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('projectDetailPage.invoiceExtraFiles')" min-width="168" align="center">
                  <template slot-scope="scope">
                    <span class="text-muted">{{ attachmentCountForInvoice(scope.row.id) }}</span>
                    <el-button
                      v-if="canManageProjectFinance"
                      type="text"
                      size="small"
                      @click="triggerLinkedAttach('invoice', scope.row.id)"
                    >{{ $t('projectDetailPage.uploadLinkedAttach') }}</el-button>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('projectDetailPage.attachmentCol')" min-width="168" align="center">
                  <template slot-scope="scope">
                    <template v-if="scope.row.fileUrl">
                      <el-button type="text" size="small" @click="openInvoicePreview(scope.row.id)">{{ $t('common.preview') }}</el-button>
                      <el-button type="text" size="small" @click="openInvoiceDownload(scope.row.id)">{{ $t('projectDetailPage.download') }}</el-button>
                    </template>
                    <template v-else-if="canManageProjectFinance">
                      <el-button type="text" size="small" @click="triggerInvoiceMainUpload(scope.row)">{{ $t('projectDetailPage.uploadMainInvoice') }}</el-button>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </template>
                </el-table-column>
              </el-table>
              <input
                ref="invoiceMainFileInput"
                type="file"
                class="hidden-file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                @change="onInvoiceMainFile"
              />
            </el-card>
          </el-tab-pane>

          <el-tab-pane :label="$t('projectPage.documents')" name="documents">
            <div class="table-head table-head--space">
              <span>{{ $t('projectPage.attachments') }}</span>
              <div>
                <input ref="attachmentInput" type="file" class="hidden-file" @change="onAttachmentFile" />
                <el-button type="primary" size="small" @click="$refs.attachmentInput.click()">{{ $t('common.upload') }}</el-button>
              </div>
            </div>
            <el-table :data="workspace.attachments" size="small" border>
              <el-table-column prop="fileName" :label="$t('projectDetailPage.attFile')" min-width="220" />
              <el-table-column :label="$t('projectDetailPage.attSource')" width="160">
                <template slot-scope="scope">{{ attachmentKindLabel(scope.row) }}</template>
              </el-table-column>
              <el-table-column :label="$t('projectDetailPage.attCreated')" width="210">
                <template slot-scope="scope">{{ formatDisplayDateTime(scope.row.createdAt) || '—' }}</template>
              </el-table-column>
              <el-table-column :label="$t('projectDetailPage.attachmentCol')" width="176">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="openAttachmentPreview(scope.row)">{{ $t('common.preview') }}</el-button>
                  <el-button type="text" size="small" @click="openAttachmentDownload(scope.row)">{{ $t('projectDetailPage.download') }}</el-button>
                </template>
              </el-table-column>
            </el-table>

            <input
              ref="linkedAttachInput"
              type="file"
              class="hidden-file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip"
              @change="onLinkedAttachFile"
            />
          </el-tab-pane>

          <el-tab-pane :label="$t('projectPage.activity')" name="activity">
            <div class="activity-list">
              <div v-for="item in workspace.activity" :key="item.type + item.ref + item.at" class="activity-item">
                <div class="activity-item__title">{{ item.title }}</div>
                <div class="activity-item__meta">{{ item.ref || '—' }} · {{ formatActivityAt(item.at) }}</div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </div>

    <form-dialog :title="$t('projectDetailPage.expenseTitle')" :visible.sync="expenseDialogVisible" width="580px">
      <el-form label-width="118px" size="small">
        <el-form-item :label="$t('projectDetailPage.expenseKind')">
          <el-radio-group v-model="expenseForm.expenseTypeCode" size="small" @change="onExpenseKindChange">
            <el-radio :label="1">{{ $t('projectDetailPage.expenseTypeVendor') }}</el-radio>
            <el-radio :label="2">{{ $t('projectDetailPage.expenseTypeSubcontractor') }}</el-radio>
            <el-radio :label="3">{{ $t('projectDetailPage.expenseTypeMisc') }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="!expenseIsOther" :label="expensePartySelectLabel" required>
          <el-select
            v-model="expenseForm.subcontractorId"
            clearable
            filterable
            :placeholder="$t('projectDetailPage.subPlaceholder')"
            style="width:100%;"
          >
            <el-option
              v-for="s in filteredExpenseParties"
              :key="s.id"
              :label="(s.companyName || s.subcontractorName) + ' / ' + (s.contactPerson || '')"
              :value="s.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="expenseIsOther" :label="$t('projectDetailPage.payee')" required>
          <el-input v-model="expenseForm.payeeName" clearable :placeholder="$t('projectDetailPage.payeeHint')" />
        </el-form-item>
        <el-form-item :label="$t('common.amount')" required>
          <el-input v-model="expenseForm.amount" clearable />
        </el-form-item>
        <el-form-item :label="$t('common.date')" required>
          <el-date-picker v-model="expenseForm.expenseDate" type="date" value-format="yyyy-MM-dd" style="width:100%;" />
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.method')" required>
          <el-input v-model="expenseForm.paymentMethod" clearable />
        </el-form-item>
        <el-form-item v-if="canManageProjectFinance" :label="$t('projectDetailPage.expenseOptionalAttachment')">
          <input
            ref="expenseFormAttachmentInput"
            type="file"
            class="hidden-file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip"
            @change="onExpenseFormAttachmentChange"
          />
          <el-button size="small" @click="$refs.expenseFormAttachmentInput && $refs.expenseFormAttachmentInput.click()">{{
            $t('common.upload')
          }}</el-button>
          <div
            v-if="expenseFormExistingAttachments.length || expenseFormAttachmentFiles.length"
            class="expense-form-attachment-list"
          >
            <div
              v-for="row in expenseFormExistingAttachments"
              :key="'saved-' + row.id"
              class="expense-form-attachment-item"
            >
              <span class="expense-form-attachment-name">{{ row.fileName || '—' }}</span>
              <el-button type="text" size="small" @click="openAttachmentPreview(row)">{{ $t('common.preview') }}</el-button>
            </div>
            <div
              v-for="(file, idx) in expenseFormAttachmentFiles"
              :key="'pending-' + idx + '-' + file.name"
              class="expense-form-attachment-item"
            >
              <span class="expense-form-attachment-name">{{ file.name }}</span>
              <el-button type="text" size="small" @click="previewExpenseFormAttachment(idx)">{{ $t('common.preview') }}</el-button>
            </div>
          </div>
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.remark')"><el-input v-model="expenseForm.remarkText" type="textarea" :rows="3" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="expenseDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="expenseSaving" @click="saveExpense">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog
      :title="$t('projectDetailPage.expenseAttachmentListTitle')"
      :visible.sync="expenseAttachmentDialogVisible"
      width="620px"
    >
      <el-table :data="expenseAttachmentDialogRows" size="small" border>
        <el-table-column prop="fileName" :label="$t('projectDetailPage.attFile')" min-width="260" show-overflow-tooltip />
        <el-table-column :label="$t('projectDetailPage.attCreated')" width="170">
          <template slot-scope="scope">{{ formatDisplayDateTime(scope.row.createdAt) || '—' }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="96" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openAttachmentPreview(scope.row)">{{ $t('common.preview') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <p v-if="!expenseAttachmentDialogRows.length" class="text-muted expense-attachment-empty">
        {{ $t('projectDetailPage.expenseAttachmentListEmpty') }}
      </p>
      <span slot="footer">
        <el-button @click="expenseAttachmentDialogVisible = false">{{ $t('common.cancel') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog
      :title="$t('projectDetailPage.paymentAttachmentListTitle')"
      :visible.sync="paymentAttachmentDialogVisible"
      width="620px"
    >
      <el-table :data="paymentAttachmentDialogRows" size="small" border>
        <el-table-column prop="fileName" :label="$t('projectDetailPage.attFile')" min-width="260" show-overflow-tooltip />
        <el-table-column :label="$t('projectDetailPage.attCreated')" width="170">
          <template slot-scope="scope">{{ formatDisplayDateTime(scope.row.createdAt) || '—' }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="96" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openAttachmentPreview(scope.row)">{{ $t('common.preview') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <p v-if="!paymentAttachmentDialogRows.length" class="text-muted expense-attachment-empty">
        {{ $t('projectDetailPage.paymentAttachmentListEmpty') }}
      </p>
      <span slot="footer">
        <el-button @click="paymentAttachmentDialogVisible = false">{{ $t('common.cancel') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog :title="$t('projectDetailPage.paymentTitle')" :visible.sync="paymentDialogVisible" width="520px" @closed="onPaymentDialogClosed">
      <el-form label-width="110px" size="small">
        <el-form-item :label="$t('common.amount')" required>
          <el-input v-model="paymentForm.amount" />
        </el-form-item>
        <el-form-item :label="$t('common.date')" required>
          <el-date-picker v-model="paymentForm.paymentDate" type="date" value-format="yyyy-MM-dd" style="width:100%;" />
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.method')" required>
          <el-input v-model="paymentForm.paymentMethod" />
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.reference')" required>
          <el-input v-model="paymentForm.referenceNo" />
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.paymentPlan')" required>
          <el-select v-model="paymentForm.paymentPlanId" clearable filterable :placeholder="$t('common.pleaseSelect')" style="width:100%;">
            <el-option v-for="plan in workspace.paymentPlans" :key="plan.id" :label="plan.nodeName" :value="plan.id" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.remark')"><el-input v-model="paymentForm.remarkText" type="textarea" :rows="3" /></el-form-item>
        <el-form-item v-if="canManageProjectFinance" :label="$t('projectDetailPage.paymentOptionalAttachment')">
          <input
            ref="paymentFormAttachmentInput"
            type="file"
            class="hidden-file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip"
            @change="onPaymentFormAttachmentChange"
          />
          <el-button size="small" @click="$refs.paymentFormAttachmentInput && $refs.paymentFormAttachmentInput.click()">{{
            $t('common.upload')
          }}</el-button>
          <div
            v-if="paymentFormExistingAttachments.length || paymentFormAttachmentFiles.length"
            class="expense-form-attachment-list"
          >
            <div
              v-for="row in paymentFormExistingAttachments"
              :key="'pay-saved-' + row.id"
              class="expense-form-attachment-item"
            >
              <span class="expense-form-attachment-name">{{ row.fileName || '—' }}</span>
              <el-button type="text" size="small" @click="openAttachmentPreview(row)">{{ $t('common.preview') }}</el-button>
            </div>
            <div
              v-for="(file, idx) in paymentFormAttachmentFiles"
              :key="'pay-pending-' + idx + '-' + file.name"
              class="expense-form-attachment-item"
            >
              <span class="expense-form-attachment-name">{{ file.name }}</span>
              <el-button type="text" size="small" @click="previewPaymentFormAttachment(idx)">{{ $t('common.preview') }}</el-button>
            </div>
          </div>
        </el-form-item>
        <el-divider content-position="left">{{ $t('projectDetailPage.paymentNewInvoiceSection') }}</el-divider>
        <el-form-item :label="$t('projectDetailPage.invoiceNoOptional')">
          <el-input v-model="paymentCreateInvoiceNo" clearable />
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.invoice')">
          <input
            ref="paymentCreateInvoiceFileInput"
            type="file"
            class="hidden-file"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            @change="onPaymentCreateInvoiceFileChange"
          />
          <el-button size="small" @click="$refs.paymentCreateInvoiceFileInput && $refs.paymentCreateInvoiceFileInput.click()">{{ $t('common.upload') }}</el-button>
          <span v-if="paymentCreateInvoiceFileName" class="payment-invoice-filename">{{ paymentCreateInvoiceFileName }}</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="paymentDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="paymentSaving" @click="savePayment">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog
      :title="$t('projectDetailPage.supplementInvoice')"
      :visible.sync="paymentInvoiceDlgVisible"
      width="520px"
      @closed="onPaymentInvoiceDlgClosed"
    >
      <el-form v-if="paymentInvoiceRow" label-width="120px" size="small">
        <el-form-item :label="$t('projectDetailPage.invoiceNoOptional')">
          <el-input v-model="paymentInvoiceNo" clearable />
        </el-form-item>
        <el-form-item :label="$t('projectDetailPage.invoice')">
          <input
            ref="paymentInvoiceFileInput"
            type="file"
            class="hidden-file"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            @change="onPaymentInvoiceFileChange"
          />
          <el-button size="small" @click="$refs.paymentInvoiceFileInput && $refs.paymentInvoiceFileInput.click()">{{ $t('common.upload') }}</el-button>
          <span v-if="paymentInvoiceFileName" class="payment-invoice-filename">{{ paymentInvoiceFileName }}</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="paymentInvoiceDlgVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="paymentInvoiceSaving" @click="savePaymentInvoice">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>
  </detail-page-shell>
</template>

<script>
import { recalcProject } from '@/api/project'
import { getSubcontractorPage } from '@/api/subcontractor'
import { createInvoice, downloadInvoiceUrl, previewInvoiceUrl, uploadInvoiceFile } from '@/api/invoice'
import { downloadSignedUrl, previewSignedUrl } from '@/api/contract'
import { downloadChangeOrderUrl } from '@/api/changeOrder'
import { downloadBlobAuthenticated, previewBlobAuthenticated } from '@/utils/downloadBlobAuth'
import {
  createPaymentRecord,
  createProjectExpense,
  deletePaymentRecord,
  deleteProjectExpense,
  downloadAttachmentUrl,
  previewAttachmentUrl,
  getProjectWorkspace,
  linkPaymentInvoice,
  updatePaymentRecord,
  updateProjectExpense,
  uploadProjectAttachment
} from '@/api/projectFinance'
import { labelChangeOrderApproval, labelPaymentPlanStatus, labelProjectStatus } from '@/utils/crmLabels'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'
import DetailPageShell from '@/components/DetailPageShell.vue'
import FormDialog from '@/components/FormDialog.vue'
import { decodeReturnQuery } from '@/utils/contractDetailNav'

export default {
  name: 'ProjectDetail',
  components: { DetailPageShell, FormDialog },
  computed: {
    projectReturnPath() {
      return decodeReturnQuery(this.$route.query.return)
    },
    projectDetailBackUsesHistory() {
      return !this.projectReturnPath
    },
    projectBackLabel() {
      return this.$t('common.back')
    },
    canManageProjectFinance() {
      const u = this.$store.state.userInfo || {}
      const roles = u.roleCodes || []
      if (roles.includes('OFFICE') || roles.includes('ADMIN')) return true
      return roles.includes('PM') && !!u.pmCanMutateProjectFinance
    },
    expensePartySelectLabel() {
      const t = Number(this.expenseForm.expenseTypeCode)
      return t === 2
        ? this.$t('projectDetailPage.selectSubcontractorParty')
        : this.$t('projectDetailPage.selectVendor')
    },
    expenseIsOther() {
      return Number(this.expenseForm && this.expenseForm.expenseTypeCode) === 3
    },
    /** 与合作方「类型」一致：采购只显示供应商+均可；承包商只显示承包商+均可 */
    filteredExpenseParties() {
      const kind = Number(this.expenseForm.expenseTypeCode)
      if (kind === 3) return []
      const rows = this.subList || []
      return rows.filter(s => {
        const t = s.partyTypeCode != null ? Number(s.partyTypeCode) : 3
        if (kind === 2) return t === 2 || t === 3
        return t === 1 || t === 3
      })
    },
    expenseAttachmentDialogRows() {
      return this.expenseAttachmentsForExpense(this.expenseAttachmentDialogExpenseId)
    },
    expenseFormExistingAttachments() {
      if (!this.expenseForm || this.expenseForm.id == null) return []
      return this.expenseAttachmentsForExpense(this.expenseForm.id)
    },
    paymentAttachmentDialogRows() {
      return this.paymentAttachmentsForPayment(this.paymentAttachmentDialogPaymentId)
    },
    paymentFormExistingAttachments() {
      if (!this.paymentForm || this.paymentForm.id == null) return []
      return this.paymentAttachmentsForPayment(this.paymentForm.id)
    }
  },
  data() {
    return {
      loading: false,
      p: null,
      tab: 'overview',
      workspace: {
        paymentPlans: [],
        invoices: [],
        changeOrders: [],
        expenses: [],
        payments: [],
        attachments: [],
        activity: []
      },
      summary: {},
      subList: [],
      expenseDialogVisible: false,
      expenseSaving: false,
      expenseForm: {},
      expenseAttachmentDialogVisible: false,
      expenseAttachmentDialogExpenseId: null,
      paymentAttachmentDialogVisible: false,
      paymentAttachmentDialogPaymentId: null,
      paymentDialogVisible: false,
      paymentSaving: false,
      paymentForm: {},
      paymentInvoiceDlgVisible: false,
      paymentInvoiceRow: null,
      paymentInvoiceNo: '',
      paymentInvoiceFile: null,
      paymentInvoiceFileName: '',
      paymentInvoiceSaving: false,
      paymentCreateInvoiceNo: '',
      paymentCreateInvoiceFile: null,
      paymentCreateInvoiceFileName: '',
      linkedAttachPending: null,
      expenseFormAttachmentFiles: [],
      paymentFormAttachmentFiles: [],
      invoiceMainPendingId: null
    }
  },
  created() {
    this.load()
  },
  methods: {
    formatUsd,
    formatDisplayDateTime,
    hasApprovedContractChange(s) {
      if (!s) return false
      const n = Number(s.changeOrderAmount)
      return !Number.isNaN(n) && n !== 0
    },
    formatActivityAt(at) {
      const s = formatDisplayDateTime(at)
      return s || '—'
    },
    labelProjectStatus,
    labelChangeOrderApproval,
    labelPaymentPlanStatus,
    async load() {
      this.loading = true
      try {
        await Promise.all([this.loadWorkspace(), this.loadSubs()])
      } finally {
        this.loading = false
      }
    },
    async loadWorkspace() {
      const r = await getProjectWorkspace(this.$route.params.id)
      const data = r.data !== undefined ? r.data : r
      this.p = data.project
      this.summary = data.summary || {}
      this.workspace = {
        paymentPlans: data.paymentPlans || [],
        invoices: data.invoices || [],
        changeOrders: data.changeOrders || [],
        expenses: data.expenses || [],
        payments: data.payments || [],
        attachments: data.attachments || [],
        activity: data.activity || []
      }
    },
    async loadSubs() {
      try {
        const r = await getSubcontractorPage({ pageNo: 1, pageSize: 200 })
        const d = r.data || r
        this.subList = d.records || []
      } catch (e) {
        this.subList = []
      }
    },
    async recalc() {
      try {
        const r = await recalcProject(this.$route.params.id)
        this.p = r.data !== undefined ? r.data : r
        this.$message.success(this.$t('projectDetailPage.recalcOk'))
        await this.loadWorkspace()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      }
    },
    labelExpenseKind(code) {
      const c = code == null ? 1 : Number(code)
      if (c === 2) return this.$t('projectDetailPage.expenseTypeSubcontractor')
      if (c === 3) return this.$t('projectDetailPage.expenseTypeMisc')
      return this.$t('projectDetailPage.expenseTypeVendor')
    },
    onExpenseKindChange() {
      const kind = Number(this.expenseForm.expenseTypeCode)
      if (kind === 3) {
        this.expenseForm.subcontractorId = null
      } else {
        this.expenseForm.payeeName = ''
        const ids = this.filteredExpenseParties.map(s => s.id)
        if (this.expenseForm.subcontractorId != null && !ids.includes(this.expenseForm.subcontractorId)) {
          this.expenseForm.subcontractorId = null
        }
      }
    },
    resolveExpensePayeeFromParty(subId) {
      if (subId == null) return ''
      const s = this.subList.find(x => x.id === subId)
      if (!s) return ''
      return (
        (s.companyName && String(s.companyName).trim()) ||
        (s.subcontractorName && String(s.subcontractorName).trim()) ||
        ''
      )
    },
    openExpenseDialog(row) {
      if (!this.canManageProjectFinance) return
      if (row) {
        const raw = row.expenseTypeCode != null ? Number(row.expenseTypeCode) : 1
        let expenseTypeCode = 1
        if (raw === 2) expenseTypeCode = 2
        else if (raw === 3) expenseTypeCode = 3
        else expenseTypeCode = 1
        this.expenseForm = {
          ...row,
          expenseTypeCode,
          subcontractorId: expenseTypeCode === 3 ? null : row.subcontractorId != null ? row.subcontractorId : null,
          amount: row.amount != null ? String(row.amount) : ''
        }
      } else {
        this.expenseForm = {
          payeeName: '',
          amount: '',
          expenseDate: '',
          paymentMethod: '',
          remarkText: '',
          expenseTypeCode: 1,
          subcontractorId: null
        }
      }
      this.expenseFormAttachmentFiles = []
      this.$nextTick(() => {
        if (this.$refs.expenseFormAttachmentInput) this.$refs.expenseFormAttachmentInput.value = ''
      })
      this.expenseDialogVisible = true
    },
    validateExpenseForm() {
      const f = this.expenseForm
      if (!f) return false
      const kind = f.expenseTypeCode != null ? Number(f.expenseTypeCode) : 1
      if (kind === 3) {
        const payee = (f.payeeName != null ? String(f.payeeName) : '').trim()
        if (!payee) {
          this.$message.warning(this.$t('projectDetailPage.expenseValidatePayee'))
          return false
        }
      } else if (f.subcontractorId == null || f.subcontractorId === '') {
        this.$message.warning(this.$t('projectDetailPage.expenseValidateParty'))
        return false
      } else if (!this.resolveExpensePayeeFromParty(f.subcontractorId)) {
        this.$message.warning(this.$t('projectDetailPage.expenseValidatePartyName'))
        return false
      }
      const amtRaw = f.amount
      if (amtRaw === '' || amtRaw == null || String(amtRaw).trim() === '') {
        this.$message.warning(this.$t('projectDetailPage.expenseValidateAmount'))
        return false
      }
      const amt = Number(String(amtRaw).trim())
      if (Number.isNaN(amt)) {
        this.$message.warning(this.$t('projectDetailPage.expenseValidateAmount'))
        return false
      }
      if (amt <= 0) {
        this.$message.warning(this.$t('projectDetailPage.expenseValidateAmountPositive'))
        return false
      }
      const date = f.expenseDate != null ? String(f.expenseDate).trim() : ''
      if (!date) {
        this.$message.warning(this.$t('projectDetailPage.expenseValidateDate'))
        return false
      }
      const method = (f.paymentMethod != null ? String(f.paymentMethod) : '').trim()
      if (!method) {
        this.$message.warning(this.$t('projectDetailPage.expenseValidateMethod'))
        return false
      }
      return true
    },
    async saveExpense() {
      if (!this.canManageProjectFinance) return
      if (!this.validateExpenseForm()) return
      this.expenseSaving = true
      try {
        const pid = Number(this.$route.params.id)
        let expenseSavedId = null
        const rawKind = Number(this.expenseForm.expenseTypeCode)
        const expenseTypeCode = rawKind === 2 ? 2 : rawKind === 3 ? 3 : 1
        const payeeName =
          expenseTypeCode === 3
            ? String(this.expenseForm.payeeName || '').trim()
            : this.resolveExpensePayeeFromParty(this.expenseForm.subcontractorId)
        const payload = {
          ...this.expenseForm,
          expenseTypeCode,
          subcontractorId: expenseTypeCode === 1 || expenseTypeCode === 2 ? this.expenseForm.subcontractorId : null,
          payeeName,
          amount: Number(String(this.expenseForm.amount).trim()),
          paymentMethod: String(this.expenseForm.paymentMethod || '').trim(),
          remarkText:
            this.expenseForm.remarkText != null && String(this.expenseForm.remarkText).trim()
              ? String(this.expenseForm.remarkText).trim()
              : null
        }
        if (payload.id) {
          await updateProjectExpense(pid, payload.id, payload)
          expenseSavedId = Number(payload.id)
          if (Number.isNaN(expenseSavedId)) expenseSavedId = null
        } else {
          const res = await createProjectExpense(pid, payload)
          expenseSavedId = res && res.data !== undefined ? res.data : res
          expenseSavedId = expenseSavedId != null ? Number(expenseSavedId) : null
          if (expenseSavedId != null && Number.isNaN(expenseSavedId)) expenseSavedId = null
        }
        if (this.expenseFormAttachmentFiles.length && expenseSavedId != null && !Number.isNaN(expenseSavedId)) {
          for (const file of this.expenseFormAttachmentFiles) {
            await uploadProjectAttachment(pid, {
              file,
              attachmentTypeCode: 4,
              expenseId: expenseSavedId
            })
          }
        }
        this.resetExpenseFormAttachment()
        await recalcProject(pid)
        this.expenseDialogVisible = false
        await this.loadWorkspace()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('projectDetailPage.saveFailed'))
      } finally {
        this.expenseSaving = false
      }
    },
    resetExpenseFormAttachment() {
      this.expenseFormAttachmentFiles = []
      this.$nextTick(() => {
        if (this.$refs.expenseFormAttachmentInput) this.$refs.expenseFormAttachmentInput.value = ''
      })
    },
    onExpenseFormAttachmentChange(e) {
      const picked = Array.from((e.target && e.target.files) || [])
      this.expenseFormAttachmentFiles = this.expenseFormAttachmentFiles.concat(picked)
      if (e.target) e.target.value = ''
    },
    previewExpenseFormAttachment(index) {
      const f = this.expenseFormAttachmentFiles[index]
      if (!f) return
      const url = window.URL.createObjectURL(f)
      window.open(url, '_blank', 'noopener')
      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000)
    },
    resetPaymentFormAttachment() {
      this.paymentFormAttachmentFiles = []
      this.$nextTick(() => {
        if (this.$refs.paymentFormAttachmentInput) this.$refs.paymentFormAttachmentInput.value = ''
      })
    },
    onPaymentFormAttachmentChange(e) {
      const picked = Array.from((e.target && e.target.files) || [])
      this.paymentFormAttachmentFiles = this.paymentFormAttachmentFiles.concat(picked)
      if (e.target) e.target.value = ''
    },
    previewPaymentFormAttachment(index) {
      const f = this.paymentFormAttachmentFiles[index]
      if (!f) return
      const url = window.URL.createObjectURL(f)
      window.open(url, '_blank', 'noopener')
      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000)
    },
    attachmentCountForExpense(expenseId) {
      if (expenseId == null) return 0
      return (this.workspace.attachments || []).filter(a => a.expenseId === expenseId).length
    },
    expenseAttachmentsForExpense(expenseId) {
      if (expenseId == null) return []
      return (this.workspace.attachments || []).filter(a => a.expenseId === expenseId)
    },
    openExpenseAttachments(row) {
      if (!row || row.id == null || this.attachmentCountForExpense(row.id) <= 0) return
      this.expenseAttachmentDialogExpenseId = row.id
      this.expenseAttachmentDialogVisible = true
    },
    attachmentCountForPayment(paymentId) {
      if (paymentId == null) return 0
      return (this.workspace.attachments || []).filter(a => a.paymentRecordId === paymentId).length
    },
    paymentAttachmentsForPayment(paymentId) {
      if (paymentId == null) return []
      return (this.workspace.attachments || []).filter(a => a.paymentRecordId === paymentId)
    },
    openPaymentAttachments(row) {
      if (!row || row.id == null || this.attachmentCountForPayment(row.id) <= 0) return
      this.paymentAttachmentDialogPaymentId = row.id
      this.paymentAttachmentDialogVisible = true
    },
    attachmentCountForInvoice(invoiceId) {
      if (invoiceId == null) return 0
      return (this.workspace.attachments || []).filter(
        a => a.invoiceId === invoiceId && a.kind === 'invoiceAttach'
      ).length
    },
    attachmentKindLabel(row) {
      const k = (row && row.kind) || 'custom'
      const key = `projectDetailPage.attachKind_${k}`
      const msg = this.$t(key)
      return msg !== key ? msg : k
    },
    triggerLinkedAttach(which, id) {
      if (!this.canManageProjectFinance) return
      this.linkedAttachPending = { which, id }
      this.$nextTick(() => {
        if (this.$refs.linkedAttachInput) this.$refs.linkedAttachInput.click()
      })
    },
    async onLinkedAttachFile(e) {
      const file = e.target.files && e.target.files[0]
      const pending = this.linkedAttachPending
      this.linkedAttachPending = null
      e.target.value = ''
      if (!file || !pending) return
      const pid = Number(this.$route.params.id)
      const p = { file, attachmentTypeCode: 4 }
      if (pending.which === 'expense') p.expenseId = pending.id
      else if (pending.which === 'payment') p.paymentRecordId = pending.id
      else if (pending.which === 'invoice') p.invoiceId = pending.id
      else return
      try {
        await uploadProjectAttachment(pid, p)
        this.$message.success(this.$t('projectDetailPage.uploadOk'))
        await this.loadWorkspace()
      } catch (err) {
        this.$message.error((err && err.message) || this.$t('projectDetailPage.uploadFail'))
      }
    },
    async removeExpense(row) {
      if (!this.canManageProjectFinance) return
      try {
        await this.$confirm(this.$t('projectDetailPage.deleteExpenseConfirm'), this.$t('common.tip'), { type: 'warning' })
        await deleteProjectExpense(this.$route.params.id, row.id)
        await recalcProject(this.$route.params.id)
        await this.loadWorkspace()
      } catch (e) {
        if (e && e.message && e.message !== 'cancel') {
          this.$message.error((e && e.message) || this.$t('projectDetailPage.deleteFail'))
        }
      }
    },
    resetPaymentCreateInvoiceFields() {
      this.paymentCreateInvoiceNo = ''
      this.paymentCreateInvoiceFile = null
      this.paymentCreateInvoiceFileName = ''
      this.$nextTick(() => {
        if (this.$refs.paymentCreateInvoiceFileInput) this.$refs.paymentCreateInvoiceFileInput.value = ''
      })
    },
    onPaymentCreateInvoiceFileChange(e) {
      const f = e.target.files && e.target.files[0]
      this.paymentCreateInvoiceFile = f || null
      this.paymentCreateInvoiceFileName = f ? f.name : ''
    },
    onPaymentDialogClosed() {
      this.resetPaymentCreateInvoiceFields()
      this.resetPaymentFormAttachment()
    },
    openPaymentDialog(row) {
      if (!this.canManageProjectFinance) return
      this.resetPaymentCreateInvoiceFields()
      this.resetPaymentFormAttachment()
      if (row && row.id) {
        this.paymentForm = {
          ...row,
          amount: row.amount != null ? String(row.amount) : '',
          paymentDate: row.paymentDate || '',
          paymentMethod: row.paymentMethod || '',
          referenceNo: row.referenceNo || '',
          paymentPlanId: row.paymentPlanId != null ? row.paymentPlanId : null,
          invoiceId: row.invoiceId != null ? row.invoiceId : null,
          remarkText: row.remarkText || ''
        }
      } else {
        this.paymentForm = {
          amount: '',
          paymentDate: '',
          paymentMethod: '',
          referenceNo: '',
          paymentPlanId: null,
          invoiceId: null,
          remarkText: ''
        }
      }
      this.paymentDialogVisible = true
    },
    paymentPlanLabel(planId) {
      if (planId == null) return '—'
      const p = (this.workspace.paymentPlans || []).find(x => x.id === planId)
      return p ? (p.nodeName || `#${p.nodeSeq}`) : `#${planId}`
    },
    async openInvoicePreview(invoiceId) {
      if (invoiceId == null) return
      try {
        await previewBlobAuthenticated(previewInvoiceUrl(invoiceId), `invoice-${invoiceId}.pdf`)
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    async openInvoiceDownload(invoiceId) {
      if (invoiceId == null) return
      try {
        await downloadBlobAuthenticated(downloadInvoiceUrl(invoiceId), 'invoice')
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    openPaymentInvoiceDialog(row) {
      if (!this.canManageProjectFinance) return
      if (!row || row.id == null) return
      if (row.invoiceId) {
        this.$message.warning(this.$t('projectDetailPage.paymentAlreadyHasInvoice'))
        return
      }
      this.paymentInvoiceRow = { ...row }
      this.paymentInvoiceNo = ''
      this.paymentInvoiceFile = null
      this.paymentInvoiceFileName = ''
      this.paymentInvoiceDlgVisible = true
      this.$nextTick(() => {
        if (this.$refs.paymentInvoiceFileInput) this.$refs.paymentInvoiceFileInput.value = ''
      })
    },
    onPaymentInvoiceFileChange(e) {
      const f = e.target.files && e.target.files[0]
      this.paymentInvoiceFile = f || null
      this.paymentInvoiceFileName = f ? f.name : ''
    },
    onPaymentInvoiceDlgClosed() {
      this.paymentInvoiceRow = null
      this.paymentInvoiceNo = ''
      this.paymentInvoiceFile = null
      this.paymentInvoiceFileName = ''
      if (this.$refs.paymentInvoiceFileInput) this.$refs.paymentInvoiceFileInput.value = ''
    },
    async savePaymentInvoice() {
      if (!this.canManageProjectFinance) return
      if (!this.paymentInvoiceRow || !this.paymentInvoiceRow.id) return
      if (!this.paymentInvoiceFile) {
        this.$message.warning(this.$t('projectDetailPage.paymentSelectInvoiceFile'))
        return
      }
      this.paymentInvoiceSaving = true
      try {
        const pid = Number(this.$route.params.id)
        const invRes = await createInvoice({
          projectId: pid,
          invoiceTypeCode: 1,
          amount: Number(this.paymentInvoiceRow.amount) || 0,
          invoiceDate: this.paymentInvoiceRow.paymentDate,
          invoiceNo: (this.paymentInvoiceNo || '').trim() || undefined
        })
        const newInvoiceId = invRes && invRes.data !== undefined ? invRes.data : invRes
        await uploadInvoiceFile(newInvoiceId, this.paymentInvoiceFile)
        await linkPaymentInvoice(pid, this.paymentInvoiceRow.id, newInvoiceId)
        this.$message.success(this.$t('projectDetailPage.paymentInvoiceLinked'))
        this.paymentInvoiceDlgVisible = false
        await this.loadWorkspace()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('projectDetailPage.saveFailed'))
      } finally {
        this.paymentInvoiceSaving = false
      }
    },
    isPaymentPlanPlanning(row) {
      return row != null && Number(row.paymentStatusCode) === 1
    },
    openReceivePaymentForPlan(plan) {
      if (!this.canManageProjectFinance) return
      if (!plan || !plan.id || !this.isPaymentPlanPlanning(plan)) return
      this.resetPaymentCreateInvoiceFields()
      this.resetPaymentFormAttachment()
      this.paymentForm = {
        amount: plan.plannedAmount != null && plan.plannedAmount !== '' ? String(plan.plannedAmount) : '',
        paymentDate: plan.plannedDate || '',
        paymentMethod: '',
        referenceNo: '',
        paymentPlanId: plan.id,
        remarkText: ''
      }
      this.paymentDialogVisible = true
    },
    validatePaymentForm() {
      const f = this.paymentForm
      if (!f) return false
      const amtRaw = f.amount
      if (amtRaw === '' || amtRaw == null || String(amtRaw).trim() === '') {
        this.$message.warning(this.$t('projectDetailPage.paymentValidateAmount'))
        return false
      }
      const amt = Number(String(amtRaw).trim())
      if (Number.isNaN(amt)) {
        this.$message.warning(this.$t('projectDetailPage.paymentValidateAmount'))
        return false
      }
      if (amt <= 0) {
        this.$message.warning(this.$t('projectDetailPage.paymentValidateAmountPositive'))
        return false
      }
      const date = f.paymentDate != null ? String(f.paymentDate).trim() : ''
      if (!date) {
        this.$message.warning(this.$t('projectDetailPage.paymentValidateDate'))
        return false
      }
      if (f.paymentPlanId == null || f.paymentPlanId === '') {
        this.$message.warning(this.$t('projectDetailPage.paymentValidatePlan'))
        return false
      }
      const method = (f.paymentMethod != null ? String(f.paymentMethod) : '').trim()
      if (!method) {
        this.$message.warning(this.$t('projectDetailPage.paymentValidateMethod'))
        return false
      }
      const ref = (f.referenceNo != null ? String(f.referenceNo) : '').trim()
      if (!ref) {
        this.$message.warning(this.$t('projectDetailPage.paymentValidateReference'))
        return false
      }
      return true
    },
    async savePayment() {
      if (!this.canManageProjectFinance) return
      if (!this.validatePaymentForm()) return
      this.paymentSaving = true
      try {
        const pid = Number(this.$route.params.id)
        let invoiceId = null
        if (this.paymentCreateInvoiceFile) {
          const invRes = await createInvoice({
            projectId: pid,
            invoiceTypeCode: 1,
            amount: Number(String(this.paymentForm.amount).trim()),
            invoiceDate: this.paymentForm.paymentDate,
            invoiceNo: (this.paymentCreateInvoiceNo || '').trim() || undefined
          })
          const newInvoiceId = invRes && invRes.data !== undefined ? invRes.data : invRes
          await uploadInvoiceFile(newInvoiceId, this.paymentCreateInvoiceFile)
          invoiceId = newInvoiceId
        }
        const payload = {
          amount: Number(String(this.paymentForm.amount).trim()),
          paymentDate: this.paymentForm.paymentDate,
          paymentMethod: String(this.paymentForm.paymentMethod || '').trim(),
          referenceNo: String(this.paymentForm.referenceNo || '').trim(),
          paymentPlanId: this.paymentForm.paymentPlanId,
          invoiceId: invoiceId || this.paymentForm.invoiceId || null,
          remarkText: (this.paymentForm.remarkText != null && String(this.paymentForm.remarkText).trim()) || null
        }
        let paymentSavedId = this.paymentForm.id != null ? Number(this.paymentForm.id) : null
        if (paymentSavedId != null && Number.isNaN(paymentSavedId)) paymentSavedId = null
        if (paymentSavedId) {
          await updatePaymentRecord(pid, paymentSavedId, payload)
        } else {
          const payRes = await createPaymentRecord(pid, payload)
          paymentSavedId = payRes && payRes.data !== undefined ? payRes.data : payRes
          paymentSavedId = paymentSavedId != null ? Number(paymentSavedId) : null
          if (paymentSavedId != null && Number.isNaN(paymentSavedId)) paymentSavedId = null
        }
        if (this.paymentFormAttachmentFiles.length && paymentSavedId != null && !Number.isNaN(paymentSavedId)) {
          for (const file of this.paymentFormAttachmentFiles) {
            await uploadProjectAttachment(pid, {
              file,
              attachmentTypeCode: 4,
              paymentRecordId: paymentSavedId
            })
          }
        }
        this.resetPaymentFormAttachment()
        await this.loadWorkspace()
        this.paymentDialogVisible = false
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('projectDetailPage.saveFailed'))
      } finally {
        this.paymentSaving = false
      }
    },
    async removePayment(row) {
      if (!this.canManageProjectFinance) return
      try {
        await this.$confirm(this.$t('projectDetailPage.deletePaymentConfirm'), this.$t('common.tip'), { type: 'warning' })
        await deletePaymentRecord(this.$route.params.id, row.id)
        await recalcProject(this.$route.params.id)
        await this.loadWorkspace()
      } catch (e) {
        if (e && e.message && e.message !== 'cancel') {
          this.$message.error((e && e.message) || this.$t('projectDetailPage.deleteFail'))
        }
      }
    },
    triggerInvoiceMainUpload(row) {
      if (!this.canManageProjectFinance || !row || row.id == null) return
      this.invoiceMainPendingId = row.id
      this.$nextTick(() => {
        if (this.$refs.invoiceMainFileInput) this.$refs.invoiceMainFileInput.click()
      })
    },
    async onInvoiceMainFile(e) {
      const file = e.target.files && e.target.files[0]
      const invId = this.invoiceMainPendingId
      this.invoiceMainPendingId = null
      if (e.target) e.target.value = ''
      if (!file || invId == null) return
      try {
        await uploadInvoiceFile(invId, file)
        this.$message.success(this.$t('projectDetailPage.uploadOk'))
        await this.loadWorkspace()
      } catch (err) {
        this.$message.error((err && err.message) || this.$t('projectDetailPage.uploadFail'))
      }
    },
    previewFirstLinkedAttachment(kind, recordId) {
      const list = this.workspace.attachments || []
      let hit
      if (kind === 'expense') {
        hit = list.find(a => a.expenseId === recordId && typeof a.id === 'number')
      } else if (kind === 'payment') {
        hit = list.find(a => a.paymentRecordId === recordId && typeof a.id === 'number')
      }
      if (!hit) {
        this.$message.warning(this.$t('projectDetailPage.attachmentDownloadUnavailable'))
        return
      }
      this.openAttachmentPreview(hit)
    },
    async onAttachmentFile(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        await uploadProjectAttachment(this.$route.params.id, { file, attachmentTypeCode: 4 })
        this.$message.success(this.$t('projectDetailPage.uploadOk'))
        await this.loadWorkspace()
      } catch (err) {
        this.$message.error((err && err.message) || this.$t('projectDetailPage.uploadFail'))
      } finally {
        e.target.value = ''
      }
    },
    async openAttachmentPreview(row) {
      const fallbackName = (row && row.fileName && String(row.fileName).trim()) || 'download.bin'
      try {
        if (typeof row.id === 'number') {
          await previewBlobAuthenticated(previewAttachmentUrl(row.id), fallbackName)
          return
        }
        if (row.kind === 'invoice' && row.invoiceId) {
          await previewBlobAuthenticated(previewInvoiceUrl(row.invoiceId), fallbackName)
          return
        }
        if (row.kind === 'contractSigned' && row.contractId) {
          await previewBlobAuthenticated(previewSignedUrl(row.contractId), fallbackName)
          return
        }
        if (row.kind === 'changeOrder' && row.changeOrderId) {
          await previewBlobAuthenticated(downloadChangeOrderUrl(row.changeOrderId), fallbackName)
          return
        }
        if (row.fileUrl && /^https?:\/\//i.test(String(row.fileUrl))) {
          window.open(row.fileUrl, '_blank')
          return
        }
        this.$message.warning(this.$t('projectDetailPage.attachmentDownloadUnavailable'))
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    async openAttachmentDownload(row) {
      const fallbackName = (row && row.fileName && String(row.fileName).trim()) || 'download.bin'
      try {
        if (typeof row.id === 'number') {
          await downloadBlobAuthenticated(downloadAttachmentUrl(row.id), fallbackName)
          return
        }
        if (row.kind === 'invoice' && row.invoiceId) {
          await downloadBlobAuthenticated(downloadInvoiceUrl(row.invoiceId), fallbackName)
          return
        }
        if (row.kind === 'contractSigned' && row.contractId) {
          await downloadBlobAuthenticated(downloadSignedUrl(row.contractId), fallbackName)
          return
        }
        if (row.kind === 'changeOrder' && row.changeOrderId) {
          await downloadBlobAuthenticated(downloadChangeOrderUrl(row.changeOrderId), fallbackName)
          return
        }
        if (row.fileUrl && /^https?:\/\//i.test(String(row.fileUrl))) {
          window.open(row.fileUrl, '_blank')
          return
        }
        this.$message.warning(this.$t('projectDetailPage.attachmentDownloadUnavailable'))
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    }
  }
}
</script>

<style scoped>
.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card__contract-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 8px;
}

.summary-card__contract-parts {
  font-size: 14px;
  font-weight: 500;
  color: #94a3b8;
  line-height: 1.3;
}

.desc-contract-parts {
  margin-left: 6px;
  font-size: 13px;
  color: #94a3b8;
}

.summary-card {
  padding: 16px 18px;
  border-radius: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
}

/* 仅卡片标题行，避免命中 strong 内的金额 span */
.summary-card > span {
  display: block;
  color: var(--color-text-sub);
  margin-bottom: 8px;
  font-size: 12px;
}

.summary-card strong {
  display: block;
  color: var(--color-text);
  font-size: 22px;
  font-weight: 700;
}

.summary-card__contract-line > span:first-child {
  font: inherit;
  font-weight: inherit;
  color: inherit;
}

.block-card {
  margin-top: 16px;
}

.financial-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-head--space {
  margin-bottom: 12px;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.activity-item {
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--color-surface-soft);
  border: 1px solid var(--color-border-soft);
}

.activity-item__title {
  color: var(--color-text);
  font-weight: 600;
}

.activity-item__meta {
  margin-top: 4px;
  color: var(--color-text-sub);
  font-size: 12px;
}

.hidden-file {
  display: none;
}

/* 付款计划表头不换行（英文如 Planned date） */
.payment-plan-table ::v-deep th .cell {
  white-space: nowrap;
}

.payment-plan-actions__empty {
  color: var(--color-text-sub);
  font-size: 12px;
}

.text-muted {
  color: var(--color-text-sub);
  font-size: 13px;
}

.payment-invoice-filename {
  margin-left: 10px;
  font-size: 12px;
  color: #94a3b8;
}

.expense-form-attachment-list {
  margin-top: 8px;
}

.expense-form-attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.6;
}

.expense-form-attachment-name {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-sub);
  font-size: 12px;
}

.payment-invoice-cell {
  display: inline-block;
  font-size: 13px;
  line-height: 1.4;
  user-select: none;
}

.payment-invoice-cell--action {
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
}

.payment-invoice-cell--action:hover {
  color: #66b1ff;
}

.payment-invoice-cell--done {
  color: #67c23a;
  cursor: pointer;
  text-decoration: underline;
}

.payment-invoice-cell--done:hover {
  color: #85ce61;
}

@media (max-width: 1100px) {
  .summary-grid,
  .financial-grid {
    grid-template-columns: 1fr;
  }
}
</style>
